import Link from 'next/link';

import { buildBreadcrumbTrail } from '@/lib/breadcrumbs/buildBreadcrumbTrail';

type BreadcrumbsProps = {
  /** Route to build the trail from. Usually the current pathname. */
  path: string;
  className?: string;
};

/**
 * Breadcrumb trail for the top of the navbar.
 *
 * Follows the WAI-ARIA APG breadcrumb pattern:
 * - `<ol>` conveys sequence and item counts.
 * - The last crumb is the current page: `aria-current="page"`, rendered as text
 *   rather than a link so it is not a link to itself.
 * - Separators are `aria-hidden` so screen readers announce only the names.
 *
 * The trail is built from the sitemap's route mapping, so it uses canonical
 * marketing URLs and matches the BreadcrumbList JSON-LD.
 */
export default function Breadcrumbs({
  path,
  className = '',
}: BreadcrumbsProps) {
  const crumbs = buildBreadcrumbTrail(path);
  if (crumbs.length < 2) return null;

  return (
    <ol
      className={`flex flex-wrap items-center gap-x-1.5 text-xs ${className}`}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        return (
          <li key={crumb.url} className="flex items-center gap-1.5">
            {isLast ? (
              <span aria-current="page" className="text-primary-500 font-bold">
                {crumb.name}
              </span>
            ) : (
              <Link href={crumb.url} className="hover:underline">
                {crumb.name}
              </Link>
            )}
            {!isLast && (
              <span aria-hidden="true" className="opacity-50">
                /
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
